// Layer: presentation. Playbook authoring (ADR 0006, FR-OC-4/5).
import { useMemo, useState } from "react";
import { playbookError } from "../application/playbook-editor";
import type { StoriaApi } from "../application/ports";
import type { GuardrailSpec, Playbook, PlaybookNode } from "../domain/types";
import { GuardrailEditor } from "./GuardrailEditor";
import { messageOf } from "./ShiftView";

interface Props {
  initial: Playbook;
  api: StoriaApi;
  /** The property the save is scoped to. The playbook graph does not carry it and the
   *  API requires it. */
  propertyId: string;
  /** True for a pilot seed shown on an unseeded property: editable, not savable. */
  readOnly?: boolean;
}

export function PlaybookEditor({ initial, api, propertyId, readOnly = false }: Props) {
  const [playbook, setPlaybook] = useState<Playbook>(initial);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Validated on every keystroke so the operator sees the problem where they made it,
  // not after a round trip.
  const validationError = useMemo(() => playbookError(playbook), [playbook]);

  const updateNode = (key: string, spec: GuardrailSpec): void => {
    setSaved(false);
    setPlaybook((current) => ({
      ...current,
      nodes: current.nodes.map((node) =>
        node.key === key && node.kind === "guardrail" ? { ...node, spec } : node,
      ),
    }));
  };

  const save = async (): Promise<void> => {
    if (readOnly || validationError) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.savePlaybook(propertyId, playbook);
      setSaved(true);
    } catch (e) {
      setSaveError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-label="Playbook editor" className="stack">
      <header className="panel-head">
        <h3>{playbook.name}</h3>
        <p className="muted">Version {playbook.version}</p>
      </header>

      <label htmlFor="playbook-name">
        Name
        <input
          id="playbook-name"
          value={playbook.name}
          onChange={(e) => {
            setSaved(false);
            setPlaybook({ ...playbook, name: e.target.value });
          }}
        />
      </label>

      <ol className="nodes">
        {playbook.nodes.map((node) => (
          <li key={node.key} className="card">
            <NodeSummary node={node} />
            {node.kind === "guardrail" ? (
              <GuardrailEditor
                value={node.spec}
                idPrefix={`node-${node.key}`}
                onChange={(spec) => updateNode(node.key, spec)}
              />
            ) : null}
          </li>
        ))}
      </ol>

      {validationError ? (
        <div className="alert" role="alert">
          {validationError}
        </div>
      ) : null}
      {saveError ? (
        <div className="alert" role="alert">
          {saveError}
        </div>
      ) : null}
      {saved ? (
        <div className="notice" role="status">
          Saved.
        </div>
      ) : null}

      <div className="actions">
        <button
          type="button"
          className="primary"
          disabled={readOnly || saving || validationError !== null}
          title={readOnly ? "Seed playbooks cannot be saved" : undefined}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save playbook"}
        </button>
      </div>
    </section>
  );
}

function NodeSummary({ node }: { node: PlaybookNode }) {
  return (
    <div className="node-head">
      <span className={`badge badge-${node.kind}`}>{node.kind}</span>
      <strong>{node.key}</strong>
      <span className="muted">{describe(node)}</span>
      {node.next.length > 0 ? (
        <span className="muted">→ {node.next.join(", ")}</span>
      ) : null}
    </div>
  );
}

function describe(node: PlaybookNode): string {
  switch (node.kind) {
    case "trigger":
      return node.spec.when
        ? `on ${node.spec.signal_kind} where ${node.spec.when}`
        : `on ${node.spec.signal_kind}`;
    case "guardrail":
      return node.spec.predicate;
    case "action":
      return `${node.spec.kind}${node.spec.auto_approve ? " (auto)" : ""}`;
  }
}
